import React from 'react';
import { cn } from '../../helpers/shadcnUtils.helper';
import { ChevronDown20Regular } from '@fluentui/react-icons'; // Fluent UI Icons
import { isValidValue } from '../../helpers';
import { CalendarLtrRegular } from '@fluentui/react-icons'; // Fluent Calendar Icon
import SpinnerIcon from '../icon/Spinner';

interface InputProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  error?: string;
  'data-state'?: 'open' | 'closed';
  'data-disabled'?: boolean;
  'data-placeholder'?: boolean;
  asCalendar?: boolean;
  isLoading?: boolean;
  validationState?: 'success' | 'error' | 'none' | 'update';
  isDirty?: boolean;
  onBlur?: (event: React.FocusEvent<HTMLButtonElement>) => void; // Add onBlur prop
  inputSize?: 'sm' | 'md' | 'lg';
}

const SelectTriggerAsChild = React.forwardRef<HTMLButtonElement, InputProps>(
  (
    {
      label,
      error,
      value,
      id,
      className,
      children,
      'data-state': dataState,
      'data-disabled': dataDisabled,
      'data-placeholder': dataPlaceholder,
      asCalendar,
      isLoading,
      validationState,
      isDirty,
      onBlur, // Receive onBlur as a prop
      inputSize = 'md',
      ...props
    },
    ref,
  ) => {
    const dynamicStyle: React.CSSProperties = {
      ...(dataDisabled && { opacity: 0.5, cursor: 'not-allowed' }),
    };

    return (
      <div className="flex flex-col gap-1">
        <div className="relative w-full cursor-pointer">
          <button
            id={id}
            value={value}
            ref={ref}
            style={{ ...dynamicStyle }}
            className={cn(
              'cursor-pointer flex justify-between w-full px-3 pt-6 pb-1 pr-3 pl-3 text-base bg-neutral-98 hover:bg-blue-95 rounded-xl focus:border focus:border-primary focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
              dataState === 'open' ? 'border border-primary' : '',
              validationState === 'success'
                ? '!border-green-50 !focus-visible:ring-green-50'
                : '',
              validationState === 'error'
                ? 'border-red-50 focus-visible:ring-red-50'
                : '',
              error
                ? 'border-red-50 focus-visible:ring-red-50'
                : '',
              validationState === 'update' ? '!border-primary' : '',
              isDirty &&
                validationState !== 'success' &&
                validationState !== 'error' &&
                !error
                ? '!border-primary'
                : '',
              inputSize === 'sm' ? 'h-12' : 'h-14',
              className,
            )}
            onBlur={onBlur} // Attach onBlur event
            {...props}
          >
            <span className={cn('mr-6 line-clamp-1', dataPlaceholder ? 'text-neutral-40' : '')}>{children}</span>

            {isLoading ? (
              <div className="absolute -translate-y-1/2 pointer-events-none right-3 top-1/2 text-blue-00">
                <SpinnerIcon />
              </div>
            ) : (
              <span
                className={cn(
                  'transition-transform duration-300 absolute right-3 top-1/2 -translate-y-1/2 block pointer-events-none',
                  !asCalendar
                    ? dataState === 'open'
                      ? 'rotate-180'
                      : 'rotate-0'
                    : '',
                )}
              >
                {asCalendar ? (
                  <CalendarLtrRegular className="w-6 h-6 text-neutral-50" />
                ) : (
                  <ChevronDown20Regular className={cn('w-6 h-6', dataState === 'open' ? 'text-primary' : 'text-neutral-50')} />
                )}
              </span>
            )}
          </button>

          <label
            htmlFor={id}
            className={cn(
              'absolute left-3 top-1/2 -translate-y-1/2 text-base text-neutral-40 transition-all duration-300 transform origin-left scale-100 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-focus:!top-1 peer-focus:translate-y-0 peer-focus:scale-75 pointer-events-none',
              isValidValue(value ? value : 0)
                ? '!top-1 translate-y-0 !scale-75 '
                : '',
            )}
          >
            {label}
          </label>
        </div>
        {error && (
          <p className="mt-1 ml-4 text-xs text-left text-red-00">{error}</p>
        )}
      </div>
    );
  },
);

SelectTriggerAsChild.displayName = 'SelectTriggerAsChild';

export { SelectTriggerAsChild };

